import { useCallback, useEffect, useId, useRef } from 'react';

import { useSaveBarStore } from './save-bar-store';

export interface SaveBarEntryOptions {
  /** The editor differs from what was last saved. */
  dirty: boolean;
  /** A save is running; the bar shows a spinner and refuses a second save. */
  saving?: boolean;
  /** Runs the editor's submit path. Rejections are swallowed here; the editor renders them. */
  onSave: () => unknown;
  /** Puts the editor back to its last saved values. */
  onDiscard: () => void;
  /** Ask "Discard all unsaved changes?" before discarding. Off by default. */
  confirmDiscard?: boolean;
  /** Keep the editor out of the bar, e.g. while the person may only read. */
  disabled?: boolean;
}

export interface SaveBarEntryHandle {
  /**
   * Lets the next navigation through although the editor is dirty: for a danger-zone delete,
   * where the record is gone and there is nothing left to save.
   */
  release: () => void;
}

/**
 * Registers any editor with the save bar: a hand-rolled dirty flag (a tree editor, a sortable
 * list) or something else without react-hook-form. Forms built on react-hook-form use
 * `useSaveBar`, which derives these options from the form state.
 */
export function useSaveBarEntry({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  confirmDiscard = false,
  disabled = false,
}: SaveBarEntryOptions): SaveBarEntryHandle {
  const id = useId();
  // The store holds stable wrappers; the latest handlers are read from here when they run.
  const callbacks = useRef({ onSave, onDiscard });
  useEffect(() => {
    callbacks.current = { onSave, onDiscard };
  }, [onSave, onDiscard]);

  useEffect(() => {
    if (disabled) return;
    const { register, unregister } = useSaveBarStore.getState();
    register({
      id,
      dirty: false,
      saving: false,
      confirmDiscard: false,
      save: () => callbacks.current.onSave(),
      discard: () => callbacks.current.onDiscard(),
    });
    return () => unregister(id);
  }, [id, disabled]);

  useEffect(() => {
    if (disabled) return;
    useSaveBarStore.getState().update(id, { dirty, saving, confirmDiscard });
  }, [id, disabled, dirty, saving, confirmDiscard]);

  const release = useCallback(() => useSaveBarStore.getState().release(), []);
  return { release };
}
